import { useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useLocation } from 'react-router-dom';
import useUser from 'store/user/useUser';
import { IPatientInfoSmall } from 'apis/admin/type';
import { getNursingRecode } from 'apis/survey/index';
import ContentsHead from './ContentsHead';
import useNotification from 'hooks/useNotification';
import ContentsBody from './ContentsBody';
import useTable from 'hooks/useTable';
import { IUpedateNursingRecord } from 'apis/survey/type';
import { getSearchQuery } from 'utils/searchQuery';
import { Box, Typography } from '@mui/material';
import useClassSection from 'store/section/useClassSection';

interface Props {
  patientInfo: IPatientInfoSmall;
}

const NurseContents = ({ patientInfo }: Props) => {
  const { student_uuid: user_id } = useUser();
  const { search } = useLocation();
  const { onBeforeSearch, onRequired } = useNotification();
  const { class_section_id } = useClassSection();

  const {
    page = 1,
    keyword,
    searchType,
    sort_method,
    size,
    start_date,
    end_date,
  } = getSearchQuery(search);

  const {
    list,
    setList,
    totalCount,
    setTotalCount,
    isLoading,
    setIsLoading,
    onPageChange,
  } = useTable<IUpedateNursingRecord>();

  const onGetList = useCallback(() => {
    setIsLoading(true);

    const hasStart = Boolean(start_date);
    const hasEnd = Boolean(end_date);
    if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
      setList([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    getNursingRecode({
      page: String(page),
      patient_id: String(patientInfo.patient_id),
      user_id,
      keyword,
      searchType,
      sort_method,
      size,
      class_section_id,
      start_date, // ex) 'YYYY-MM-DDT00:00:00'
      end_date, // ex) 'YYYY-MM-DDT23:59:59' (종료일 포함)
    })
      .then(({ data }) => {
        setList(data.student_nursing_record_infos);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `가상환자 목록을 불러오려면,`;
        onBeforeSearch(message);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [
    page,
    keyword,
    searchType,
    sort_method,
    class_section_id,
    size,
    patientInfo.patient_id,
    start_date,
    end_date,
  ]);

  useEffect(onGetList, [onGetList]);

  const printableRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef: printableRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          font-size: 14pt;
        }
      }
    `,
  });

  const PatientInfo = () => {
    const { patient_id, name, age } = patientInfo;
    return (
      <Box
        className="patient-info"
        display="flex"
        gap="8px"
        sx={{
          mb: '12px',
          display: 'none',
          '@media print': {
            display: 'flex',
          },
        }}
      >
        <Typography>{patient_id}</Typography>
        <Typography sx={{ fontSize: '18px' }}>{name}</Typography>
        <Typography sx={{ fontSize: '18px' }}>{age}</Typography>
      </Box>
    );
  };

  return (
    <Box flex={2} display="flex" flexDirection="column" overflow="auto">
      <ContentsHead
        onRequired={onRequired}
        totalCount={totalCount}
        currentSearchType={searchType as any}
        currentSortType={sort_method as any}
        currentSize={size as any}
        currentStartDate={start_date as any}
        currentEndDate={end_date as any}
        setList={setList}
        setTotalCount={setTotalCount}
        setIsLoading={setIsLoading}
        patientInfo={patientInfo}
        onPrint={reactToPrintFn}
      />

      <div id="printable-area" ref={printableRef}>
        <PatientInfo />
        <ContentsBody
          totalCount={totalCount}
          list={list}
          isLoading={isLoading}
          page={Number(page) - 1}
          onPageChange={onPageChange}
        />
      </div>
    </Box>
  );
};

export default NurseContents;
